<template>
  <div class="tenant-admin-container">
    <!-- Page Header -->
    <div class="page-header py-4">
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">Tenant Selection</h1>
          <p class="page-subtitle">Switch between your business entities to manage their specific resources</p>
        </div>
      </div>
    </div>

    <!-- Navigation Section -->
    <div v-if="selectedTenant" class="navigation-grid-section">
      <div class="section-heading mb-4">
        <div class="d-flex align-items-center gap-3">
          <div class="step-badge">1</div>
          <div>
            <h5 class="m-0 fw-bold">Management Modules</h5>
            <small class="text-muted">Direct access to resource management for "{{ selectedTenant.name }}"</small>
          </div>
        </div>
      </div>

      <div class="row g-4">
        <!-- ... cards ... -->
        <div class="col-lg-4 col-md-6">
          <router-link :to="{ name: 'ExtranetBranch' }" class="module-card-link">
            <div class="module-card">
              <div class="module-icon-box branches">
                <i class="fas fa-map-marked-alt"></i>
              </div>
              <div class="module-info">
                <h6 class="module-title">Branch Locations</h6>
                <p class="module-desc">Configure physical outlets and operational zones</p>
              </div>
              <div class="module-footer">
                <span>Manage Branches</span>
                <i class="fas fa-chevron-right"></i>
              </div>
            </div>
          </router-link>
        </div>

        <div class="col-lg-4 col-md-6">
          <router-link :to="{ name: 'ExtranetMerchant' }" class="module-card-link">
            <div class="module-card">
              <div class="module-icon-box merchants">
                <i class="fas fa-cash-register"></i>
              </div>
              <div class="module-info">
                <h6 class="module-title">Merchant Accounts</h6>
                <p class="module-desc">Handle merchant profiles and point-of-sale settings</p>
              </div>
              <div class="module-footer">
                <span>Manage Merchants</span>
                <i class="fas fa-chevron-right"></i>
              </div>
            </div>
          </router-link>
        </div>

        <div class="col-lg-4 col-md-6">
          <router-link :to="{ name: 'ExtranetConnections' }" class="module-card-link">
            <div class="module-card">
              <div class="module-icon-box connections">
                <i class="fas fa-project-diagram"></i>
              </div>
              <div class="module-info">
                <h6 class="module-title">Gateway Connections</h6>
                <p class="module-desc">Setup API keys and external third-party integrations</p>
              </div>
              <div class="module-footer">
                <span>Manage Connections</span>
                <i class="fas fa-chevron-right"></i>
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Empty State (No Tenant Selected) -->
    <div v-else class="selection-empty-state">
      <div class="art-container">
        <div class="pulse-ring"></div>
        <i class="fas fa-layer-group"></i>
      </div>
      <h3>Ready to Manage</h3>
      <p>Please select a business tenant from the navbar above to unlock resource management modules.</p>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

export default {
  name: 'ExtranetTenantAdmin',

  components: {
  },

  computed: {
    ...mapState({
      selectedTenant: state => state.extranet.selectedTenant
    }),

    ...mapGetters({
      hasTenantSelected: 'extranet/hasTenantSelected'
    })
  },

  methods: {
    ...mapActions({
      selectTenant: 'extranet/selectTenant',
      clearTenant: 'extranet/clearTenant'
    }),

    handleTenantSelect(tenant) {
      if (tenant && tenant.domain) {
        this.selectTenant(tenant)
      }
    },

    handleClearSelection() {
      this.clearTenant()
    }
  }
}
</script>

<style scoped>
.tenant-admin-container {
    max-width: 1200px;
    margin: 0 auto;
    padding-bottom: 60px;
}

/* Page Header */
.page-header { margin-bottom: 8px; }
.page-title {
    font-size: 28px;
    font-weight: 800;
    color: #0f172a;
    margin: 0 0 4px 0;
    letter-spacing: -0.8px;
}
.page-subtitle { font-size: 15px; color: #64748b; margin: 0; }

/* Selection Card */
.selection-card {
    background: white;
    border-radius: var(--extranet-rounded);
    border: 1px solid var(--extranet-accent);
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.02);
}

.card-header-ext {
    padding: 20px 24px;
    background: #f8fafc;
    border-bottom: 1px solid #f1f5f9;
}

.step-badge {
    width: 32px;
    height: 32px;
    background: #1e293b;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
}

.card-body-ext {
    padding: 32px;
}

.selector-wrapper {
    max-width: 600px;
}

/* Entity Context Card */
.entity-context-card {
    background: white;
    border-radius: var(--extranet-rounded);
    border: 1px solid var(--extranet-accent);
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0,0,0,0.04);
}

.context-banner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 32px;
    background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
}

.banner-left {
    display: flex;
    align-items: center;
    gap: 24px;
}

.entity-avatar {
    width: 64px;
    height: 64px;
    background: var(--extranet-primary);
    background: linear-gradient(135deg, #fc6666 0%, #ff8e8e 100%);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 28px;
    box-shadow: 0 8px 16px rgba(252, 102, 102, 0.2);
}

.entity-identity {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.entity-name {
    font-size: 24px;
    font-weight: 800;
    color: #1e293b;
    margin: 0;
    letter-spacing: -0.5px;
}

.entity-meta {
    display: flex;
    align-items: center;
    gap: 12px;
}

.meta-tag {
    font-size: 13px;
    color: #64748b;
    font-weight: 600;
    background: #f1f5f9;
    padding: 4px 12px;
    border-radius: 6px;
}

.meta-tag.code {
    background: #fef2f2;
    color: var(--extranet-primary);
    border: 1px solid #fee2e2;
}

.status-pill-ext {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
}

.status-pill-ext.active { background: #dcfce7; color: #166534; }

.btn-reset-selection {
    background: white;
    border: 1px solid var(--extranet-accent);
    color: #64748b;
    padding: 10px 20px;
    border-radius: var(--extranet-rounded);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-reset-selection:hover {
    background: #f1f5f9;
    color: #dc2626;
    border-color: #fecaca;
}

/* Module Cards */
.module-card-link { text-decoration: none; display: block; height: 100%; }

.module-card {
    background: white;
    border: 1px solid var(--extranet-accent);
    border-radius: var(--extranet-rounded);
    padding: 32px;
    height: 100%;
    display: flex;
    flex-direction: column;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.module-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; width: 100%; height: 4px;
    background: var(--extranet-primary);
    opacity: 0;
    transition: opacity 0.3s;
}

.module-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 30px rgba(0,0,0,0.08);
    border-color: var(--extranet-primary);
}

.module-card:hover::before { opacity: 1; }

.module-icon-box {
    width: 60px;
    height: 60px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    color: white;
    margin-bottom: 24px;
}

.module-icon-box.branches { background: linear-gradient(135deg, #1e293b 0%, #334155 100%); }
.module-icon-box.merchants { background: linear-gradient(135deg, #fc6666 0%, #f43f5e 100%); }
.module-icon-box.connections { background: linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%); }

.module-title {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 8px;
}

.module-desc {
    font-size: 14px;
    color: #64748b;
    line-height: 1.6;
    margin-bottom: 24px;
    flex-grow: 1;
}

.module-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 20px;
    border-top: 1px solid #f1f5f9;
    color: #1e293b;
    font-weight: 700;
    font-size: 14px;
}

.module-card:hover .module-footer { color: var(--extranet-primary); }

/* Empty State */
.selection-empty-state {
    padding: 100px 40px;
    text-align: center;
}

.art-container {
    position: relative;
    width: 100px;
    height: 100px;
    margin: 0 auto 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 48px;
    color: #cbd5e1;
}

.pulse-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border: 4px solid #f1f5f9;
    border-radius: 50%;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% { transform: scale(1); opacity: 1; }
    100% { transform: scale(1.5); opacity: 0; }
}

.selection-empty-state h3 { font-size: 22px; font-weight: 800; color: #1e293b; margin-bottom: 12px; }
.selection-empty-state p { color: #64748b; font-size: 15px; max-width: 450px; margin: 0 auto; }

/* Transitions */
.fade-slide-enter-active, .fade-slide-leave-active { transition: all 0.4s; }
.fade-slide-enter-from { opacity: 0; transform: translateY(20px); }
.fade-slide-leave-to { opacity: 0; transform: translateY(-20px); }

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

@media (max-width: 768px) {
    .context-banner { flex-direction: column; align-items: flex-start; gap: 24px; }
    .btn-reset-selection { width: 100%; }
}
</style>
